import { useEffect, useState } from "react";
import { Activity, PauseCircle, AlertTriangle, RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";

/**
 * Polls GET /api/simulator/status every 6 seconds and renders a compact live badge
 * indicating whether the current project is being simulated, running via env default,
 * or intentionally OFF. Fades to red if the tick loop crashed.
 *
 * Props:
 *   - projectId: the currently-selected project id (badge focuses on this one).
 *   - variant: "pill" (default) | "row"
 */
export default function SimulatorStatusBadge({ projectId, variant = "pill" }) {
  const [status, setStatus] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [_clockTick, setClockTick] = useState(0); // forces re-render each second so ageSec is fresh

  useEffect(() => {
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const { data } = await api.get("/simulator/status");
        if (!cancelled) setStatus(data);
      } catch {
        if (!cancelled) setStatus(null);
      }
    };
    fetchStatus();
    const id = setInterval(fetchStatus, 6000);
    const clock = setInterval(() => setClockTick((t) => t + 1), 1000);
    return () => { cancelled = true; clearInterval(id); clearInterval(clock); };
  }, []);

  if (!status) {
    return (
      <Badge variant="outline" className="text-[10px] gap-1" data-testid="sim-badge-loading">
        <RefreshCw className="w-3 h-3 animate-spin" /> Sim status…
      </Badge>
    );
  }

  const proj = (status.projects || []).find((p) => p.id === projectId);
  const eff = proj?.effective || (status.env_default ? "on-env" : "off-env");

  // Age of last tick — turns red if simulator loop stalled (>15s)
  let ageSec = null;
  if (status.last_tick_at) {
    ageSec = Math.floor((Date.now() - new Date(status.last_tick_at).getTime()) / 1000);
  }
  const stalled = status.running && ageSec !== null && ageSec > 15;

  // Style dispatch
  let cls = "text-[10px] gap-1";
  let icon = <Activity className="w-3 h-3" />;
  let label = "Simulator ON";
  let title = "";
  let testId = "sim-badge-on";

  if (!status.running || stalled) {
    cls += " border-destructive/50 text-destructive";
    icon = <AlertTriangle className="w-3 h-3" />;
    label = stalled ? `Sim stalled (${ageSec}s)` : "Sim not running";
    title = status.last_error || "Simulator task not ticking";
    testId = "sim-badge-error";
  } else if (eff === "on-forced") {
    cls += " border-primary/50 text-primary bg-primary/5";
    label = "Simulator FORCE ON";
    title = "Project override: paksa ON";
    testId = "sim-badge-force-on";
  } else if (eff === "on-env") {
    cls += " border-primary/40 text-primary";
    label = "Simulator ON (env)";
    title = "Ikut env SIMULATE_SENSORS=true (default)";
    testId = "sim-badge-env-on";
  } else if (eff === "off-forced") {
    cls += " border-accent/60 text-accent";
    icon = <PauseCircle className="w-3 h-3" />;
    label = "Simulator OFF (ESP32 only)";
    title = "Project override: paksa OFF";
    testId = "sim-badge-force-off";
  } else {
    cls += " border-muted-foreground/40 text-muted-foreground";
    icon = <PauseCircle className="w-3 h-3" />;
    label = "Simulator OFF (env)";
    title = "env SIMULATE_SENSORS=false";
    testId = "sim-badge-env-off";
  }

  if (variant === "row") {
    return (
      <div className={`inline-flex items-center gap-1.5 text-xs ${cls.replace("text-[10px]", "")}`} title={title} data-testid={testId + "-row"}>
        {icon}
        <span>{label}</span>
        {status.running && !stalled && ageSec !== null && (
          <span className="text-muted-foreground font-mono ml-1">· tick {status.tick} · {ageSec}s ago</span>
        )}
      </div>
    );
  }

  return (
    <Badge variant="outline" className={cls} title={title} data-testid={testId}>
      {icon}
      {label}
    </Badge>
  );
}
